import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { checkFeatureFlag, FEATURE_FLAGS } from '../../../shared/utils/featureFlags';
import { sendPayout } from '../../cycle-engine';

const PayoutForm = ({ hand, groupData, allHands, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [flagEnabled, setFlagEnabled] = useState(false);
  const [flagLoading, setFlagLoading] = useState(true);
  const [formData, setFormData] = useState({
    payment_method: 'zelle',
    reference_number: '',
    date_sent: new Date().toISOString().split('T')[0],
    note: '',
  });
  const [memberData, setMemberData] = useState(null);
  const [totalPool, setTotalPool] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [calculatedPayout, setCalculatedPayout] = useState(0);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkFlag = async () => {
      const enabled = await checkFeatureFlag(FEATURE_FLAGS.SUSU_ROUNDS);
      setFlagEnabled(enabled);
      setFlagLoading(false);
    };
    checkFlag();
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // Member name for the collector
        if (hand.member_db_id) {
          const { data: member } = await supabaseClient
            .from('members')
            .select('name, email')
            .eq('id', hand.member_db_id)
            .single();
          setMemberData(member);
        }

        // Pool = sum of ACTIVE hands' contribution amounts, EXCLUDING the
        // collector's own collecting hand (Model B: the collector does not
        // contribute to the pot they collect). This is the OTHERS' total =
        // the engine's v_pool. `hand` is the collector receiving this payout.
        //   * is_active filter (Layer 1): a deselected hand never inflates the pool.
        //   * collector exclusion (Model B / Stage 12): without it the pool is the
        //     full N-hand sum ($30), which send_payout's clamp rejects as > v_pool
        //     ($20) — the pre-fill must be the others' total.
        const activeHands = allHands.filter(h => h.is_active !== false);
        const pool = activeHands
          .filter(h => String(h.id) !== String(hand.id))
          .reduce((sum, h) => sum + (h.contribution_amount || 0), 0);
        setTotalPool(pool);

        // Outstanding = sum of amount_expected for the collector's ACTIVE hands
        // where NOT paid/covered (netted from the pool for the record).
        const collectorHandIds = activeHands
          .filter(h => h.member_db_id === hand.member_db_id)
          .map(h => h.id);

        let outstanding = 0;
        if (collectorHandIds.length > 0) {
          const { data: unpaid } = await supabaseClient
            .from('payments')
            .select('amount_expected')
            .in('hand_id', collectorHandIds)
            .not('status', 'in', '(paid,covered)');
          outstanding = unpaid?.reduce((sum, p) => sum + (p.amount_expected || 0), 0) || 0;
        }
        setTotalOutstanding(outstanding);

        const payout = pool - outstanding;
        setCalculatedPayout(payout);
        setPayoutAmount(String(payout));
      } catch (err) {
        console.error('Error initializing payout data:', err);
      }
    };
    initializeData();
  }, [hand, allHands]);

  // Compare in cents to avoid floating-point false positives
  const enteredAmount = parseFloat(payoutAmount) || 0;
  const isOverridden = Math.round(enteredAmount * 100) !== Math.round(calculatedPayout * 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (isOverridden && !adjustmentReason.trim()) {
      setError('A reason is required when the payout amount differs from the calculated value.');
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      // Route through the cycle engine: send_payout (SECURITY DEFINER) guards
      // funding for the LIVE round, computes total_pool / collector_contribution,
      // writes the payout, and flips the round to paid_out — atomically + audited.
      // The client no longer writes payouts directly, and hands no longer carry
      // contribution_status.
      const payoutData = await sendPayout(hand.id, enteredAmount);

      // send_payout(hand_id, amount) does not capture the admin-entered
      // method/reference/date/note (cosmetic metadata the RPC signature omits),
      // so attach them to the payout the RPC just created. Allowed by the admin
      // RLS policy + guard trigger (admin path). See BANK note: optionally extend
      // send_payout to accept this metadata directly.
      const { error: metaError } = await supabaseClient
        .from('payouts')
        .update({
          payment_method: formData.payment_method,
          reference_number: formData.reference_number || null,
          date_sent: formData.date_sent,
          note: formData.note || null,
        })
        .eq('id', payoutData.id);
      if (metaError) throw metaError;

      // Audit log — split by whether admin overrode the calculated amount
      if (isOverridden) {
        await supabaseClient.from('audit_logs').insert({
          actor_id: user.id,
          event_category: 'payout',
          event_type: 'payout_amount_overridden',
          actor_role: 'admin',
          old_value: { original_calculated_amount: calculatedPayout },
          new_value: {
            admin_entered_amount: enteredAmount,
            adjustment_reason: adjustmentReason.trim(),
          },
          risk_level: 'high',
          flagged: true,
          timestamp: now,
          metadata: {
            payout_id: payoutData.id,
            group_id: groupData.id,
            hand_id: hand.id,
            original_calculated_amount: calculatedPayout,
            admin_entered_amount: enteredAmount,
            adjustment_reason: adjustmentReason.trim(),
          },
        });
      } else {
        await supabaseClient.from('audit_logs').insert({
          actor_id: user.id,
          event_category: 'payout',
          event_type: 'payout_initiated',
          actor_role: 'admin',
          new_value: { calculated_amount: calculatedPayout },
          risk_level: 'high',
          flagged: false,
          timestamp: now,
          metadata: {
            payout_id: payoutData.id,
            group_id: groupData.id,
            hand_id: hand.id,
            amount: enteredAmount,
          },
        });
      }

      onSuccess?.(payoutData);
      onClose?.();
    } catch (err) {
      console.error('Error initiating payout:', err);
      setError('Failed to initiate payout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (flagLoading) return <div className="text-center py-4">Loading...</div>;
  if (!flagEnabled) return <div className="text-center py-4">Feature not available</div>;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg max-w-md w-full p-6 max-h-screen overflow-y-auto">
        <h2 className="text-xl font-bold text-neutral-900 mb-4">Send Payout</h2>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Collector */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Collector</label>
            <input
              type="text"
              value={memberData?.name || ''}
              disabled
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 bg-neutral-50"
            />
          </div>

          {/* Payout breakdown */}
          <div className="bg-neutral-100 border border-primary-500 rounded-lg p-4 space-y-2">
            <p className="text-xs font-semibold text-primary-500 uppercase tracking-wide mb-1">Payout Breakdown</p>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Total Pool</span>
              <span className="font-medium text-neutral-900">${totalPool.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Your Outstanding</span>
              <span className={`font-medium ${totalOutstanding > 0 ? 'text-error-600' : 'text-neutral-900'}`}>
                ${totalOutstanding.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm font-semibold border-t border-neutral-300 pt-2">
              <span className="text-neutral-700">Calculated Payout</span>
              <span className="text-neutral-900">${calculatedPayout.toFixed(2)}</span>
            </div>
          </div>

          {/* Editable payout amount */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Payout Amount
              {isOverridden && (
                <span className="ml-2 text-xs font-normal text-warning-600">modified from calculated</span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={payoutAmount}
              onChange={e => {
                setPayoutAmount(e.target.value);
                setAdjustmentReason('');
              }}
              className={`w-full border rounded-lg px-3 py-2 font-semibold ${
                isOverridden ? 'border-warning-400 bg-warning-50' : 'border-neutral-300'
              }`}
              required
            />
          </div>

          {/* Reason — required only when admin changes the amount */}
          {isOverridden && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Reason for Adjustment <span className="text-error-500">*</span>
              </label>
              <textarea
                value={adjustmentReason}
                onChange={e => setAdjustmentReason(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                rows={3}
                placeholder="Explain why the payout amount differs from the calculated value..."
                required
              />
            </div>
          )}

          {/* Payment method */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
            <select
              value={formData.payment_method}
              onChange={e => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              required
            >
              <option value="zelle">Zelle</option>
              <option value="venmo">Venmo</option>
              <option value="cashapp">CashApp</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Reference Number (Optional)</label>
            <input
              type="text"
              value={formData.reference_number}
              onChange={e => setFormData({ ...formData, reference_number: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              placeholder="Transaction reference"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Date Sent</label>
            <input
              type="date"
              value={formData.date_sent}
              onChange={e => setFormData({ ...formData, date_sent: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Note to Collector (Optional)</label>
            <textarea
              value={formData.note}
              onChange={e => setFormData({ ...formData, note: e.target.value })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2"
              rows={3}
              placeholder="Add any notes about this payout..."
            />
          </div>

          {error && <p className="text-error-600 text-sm">{error}</p>}

          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary-600 text-neutral-50 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Payout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayoutForm;
